import { useCallback, useState, type ReactElement, type SyntheticEvent } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  SvgIcon,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, People, Videocam } from '@mui/icons-material';
import { MdOutlineInput, MdOutlineScreenShare } from 'react-icons/md';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow, getOnAirLivekitRoomIdFromFlow } from '@/helpers/entity-ref.helper';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { PerformCategoryModalContent } from './perform-category-modal-content';
import type { PerformCategory } from './perform-upper-buttons';
import { PerformEqualSpreadIconRow, performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';

const FOOTER_CATEGORIES: {
  id: Extract<PerformCategory, 'cam' | 'remote' | 'speakers'>;
  labelKey: TranslationKey;
  defaultLabel: string;
  icon: ReactElement;
}[] = [
  {
    id: 'cam',
    labelKey: 'flows.detail.perform.upperButtons.cam' as TranslationKey,
    defaultLabel: 'Local Cameras',
    icon: <Videocam />,
  },
  {
    id: 'remote',
    labelKey: 'flows.detail.perform.upperButtons.remote' as TranslationKey,
    defaultLabel: 'Media Laptops',
    icon: <SvgIcon component={MdOutlineScreenShare} inheritViewBox />,
  },
  {
    id: 'speakers',
    labelKey: 'flows.detail.perform.upperButtons.speakers' as TranslationKey,
    defaultLabel: 'Remote Speakers',
    icon: <People />,
  },
];

type FooterCategoryId = (typeof FOOTER_CATEGORIES)[number]['id'];

const LAST_TAB_STORAGE_KEY = 'perform.categoryFooter.lastTab';

function readLastCategoryTab(): FooterCategoryId {
  try {
    const raw = localStorage.getItem(LAST_TAB_STORAGE_KEY);
    if (raw === 'cam' || raw === 'remote' || raw === 'speakers') {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return 'cam';
}

function writeLastCategoryTab(id: FooterCategoryId) {
  try {
    localStorage.setItem(LAST_TAB_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export interface PerformCategoryFooterButtonsProps {
  flow: FlowDto;
  selectedSlot?: FlowSlotDto;
  eventSlotId?: string;
  liveContextState?: LiveContextStateDto;
  /** Lower controls: equal-width touch targets across the half-row on mobile. */
  equalSpreadOnMobile?: boolean;
  /**
   * When true (default), collapse the three LiveKit icons into one opener on xs
   * and show a tabbed modal. sm+ keeps the three inline icons.
   */
  collapseOnNarrow?: boolean;
}

export function PerformCategoryFooterButtons({
  flow,
  selectedSlot,
  eventSlotId,
  liveContextState,
  equalSpreadOnMobile = false,
  collapseOnNarrow = true,
}: PerformCategoryFooterButtonsProps) {
  const t = useTranslations();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const collapsed = collapseOnNarrow && isNarrow;

  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FooterCategoryId>(() => readLastCategoryTab());

  const flowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flow?.liveContext) : undefined;
  const livekitRoomId = flowId ? getOnAirLivekitRoomIdFromFlow(flowId, flow?.liveContext) : undefined;

  const openModal = useCallback((tab: FooterCategoryId) => {
    setActiveTab(tab);
    writeLastCategoryTab(tab);
    setModalOpen(true);
  }, []);

  const handleTabChange = (_: SyntheticEvent, value: FooterCategoryId) => {
    setActiveTab(value);
    writeLastCategoryTab(value);
  };

  const collapsedLabel = t('flows.detail.perform.lowerControls.inputSources', undefined, 'Input sources');

  const inlineButtons = FOOTER_CATEGORIES.map(({ id, labelKey, defaultLabel, icon }) => {
    const label = t(labelKey, undefined, defaultLabel);
    return (
      <Tooltip key={id} title={label}>
        <IconButton
          size="medium"
          onClick={() => openModal(id)}
          aria-label={label}
          sx={equalSpreadOnMobile ? performEqualSpreadIconButtonSx : undefined}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  });

  return (
    <>
      {collapsed ? (
        <Tooltip title={collapsedLabel}>
          <IconButton
            size="medium"
            onClick={() => openModal(activeTab)}
            aria-label={collapsedLabel}
            sx={{ height: 48, width: 48, borderRadius: 1 }}
          >
            <SvgIcon component={MdOutlineInput} inheritViewBox />
          </IconButton>
        </Tooltip>
      ) : equalSpreadOnMobile ? (
        <PerformEqualSpreadIconRow>{inlineButtons}</PerformEqualSpreadIconRow>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>{inlineButtons}</Box>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          {collapsedLabel}
          <IconButton aria-label="close" onClick={() => setModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          {FOOTER_CATEGORIES.map(({ id, labelKey, defaultLabel, icon }) => (
            <Tab
              key={id}
              value={id}
              icon={icon}
              iconPosition="start"
              label={t(labelKey, undefined, defaultLabel)}
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          ))}
        </Tabs>
        <DialogContent sx={{ pt: 2 }}>
          <PerformCategoryModalContent
            category={activeTab}
            flow={flow}
            selectedSlot={selectedSlot}
            entityRef={entityRef}
            eventSlotId={eventSlotId}
            livekitRoomId={livekitRoomId}
            liveContextState={liveContextState}
            onTrackSelected={() => setModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
